import React, { useEffect, useState } from "react";
import { IoGlobeOutline, IoSearch } from "react-icons/io5";
import { FiTrash2 } from "react-icons/fi";
import { motion } from "framer-motion";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { DAY, server } from "../../server";
import { useDebounce } from "../../hooks/useDebounce";
import LoadingComponent from "../../Components/LoadingComponent";

export default function DomainsList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [domainsList, setDomainsList] = useState([]);
  const [domainsCount, setDomainsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchParams, setSearchParams] = useState("");

  const debounceValue = useDebounce(searchParams, 500);

  useEffect(() => {
    getAllDomains();
  }, [pageNo, pageSize, debounceValue]);

  const getAllDomains = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/domain/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          pageNo,
          pageSize,
          searchParams,
          deleted: false,
        },
      });

      setDomainsList(data?.data || []);
      setDomainsCount(data?.count || 0);
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this domain?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      await axios.delete(`${server}/domain/deleteById`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          domainId,
        },
      });
      toast.success("Domain deleted successfully!");
      getAllDomains();
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <IoGlobeOutline className="text-3xl text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Domains List</h1>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search domains..."
              value={searchParams}
              onChange={(e) => setSearchParams(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-300 focus:outline-none"
            />
            <IoSearch className="absolute left-3 top-2.5 text-gray-400" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">#</th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Domain Name
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  IP Address
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Expiration Date
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Expires In
                </th>
                <th className="text-center p-3 font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {domainsList.length > 0 ? (
                domainsList.map((domain, index) => (
                  <tr
                    key={domain.id}
                    className={`border-b hover:bg-gray-50 transition ${
                      (domain.expirationDate - Date.now()) / DAY < 7 &&
                      "bg-rose-300"
                    } ${
                      (domain.expirationDate - Date.now()) / DAY < 30 &&
                      "bg-amber-300"
                    }`}
                  >
                    <td className="p-3 text-gray-700">
                      {(pageNo - 1) * pageSize + index + 1}
                    </td>
                    <td className="p-3 text-gray-800 font-medium">
                      {domain.domainName}
                    </td>
                    <td className="p-3 text-gray-800 font-medium">
                      {domain.ipAddress}
                    </td>
                    <td className="p-3 text-gray-600">
                      {domain.expirationDate
                        ? new Date(domain.expirationDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="p-3 text-gray-600 font-semibold">
                      {((domain.expirationDate - Date.now()) / DAY).toFixed(
                        0
                      ) || "—"}{" "}
                      Days
                    </td>
                    <td className="p-3 text-center">
                      <button
                        className="p-2 rounded-full hover:bg-red-100 text-red-600 transition"
                        onClick={() => handleDeleteDomain(domain?.id)}
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-5 text-center text-gray-500">
                    No domains found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {domainsCount > 0 && (
          <div className="flex justify-end mt-6">
            <Pagination
              count={Math.ceil(domainsCount / pageSize)}
              page={pageNo}
              onChange={(event, value) => setPageNo(value)}
              color="primary"
              shape="rounded"
            />
          </div>
        )}
      </motion.div>

      {loading && <LoadingComponent />}
    </div>
  );
}
